import {Injectable} from '@angular/core';

import {OrgInfo, Refmodel, constants} from '@q9elements/ui-kit/common';

import {get} from 'lodash';

import {IGridColumn} from '../../custom-grid/models/grid-column.interface';
import {
  MyMetadataDataPrivacyColumn,
  MyMetadataImpactColumn,
  MyMetadataLinksColumn,
  MyMetadataNameColumn,
  MyMetadataOptimizeStatusColumn,
  MyMetadataStatusColumn,
  MyMetadataTagsColumn,
  MyMetadataTypeColumn
} from '../components/my-metadata-view/enums/my-metadata.enum';
import {
  TreeGridDataPrivacyColumn,
  TreeGridImpactColumn,
  TreeGridLinksColumn,
  TreeGridNameColumn,
  TreeGridOptimizeStatusColumn,
  TreeGridStatusColumn,
  TreeGridTagsColumn
} from '../components/node-tree-grid/enums/tree-grid-context-menu.enum';
import {ITreeNode} from '../models/grid-node.interface';

@Injectable()
export class ColumnsGenerateService {
  public generateTreeGridColumns(
    orgInfo: OrgInfo,
    refModel: Refmodel,
    tagsColumnWidth: number
  ): IGridColumn<ITreeNode>[] {
    const isRefModel = Boolean(refModel);
    const isExternal = isRefModel && refModel.external;
    const GDPREnabled = isRefModel && refModel.gdprEnabled;
    const {packageVersion} = orgInfo || {};
    const packageNotConnected = packageVersion === constants.PACKAGE_NOT_CONNECTED;

    return [
      {
        ...TreeGridNameColumn,
        getHtmlTemplate(dataItem) {
          return dataItem.customId && dataItem.name
            ? `${dataItem.customId} : ${dataItem.name}`
            : dataItem.name || dataItem.customId;
        },
        customClass(dataItem) {
          const classes = [];

          if (dataItem.hasChildren) {
            classes.push('strong');
          }

          if (dataItem.customId || dataItem.name) {
            classes.push('main-grid-text');
          }

          return classes;
        }
      },
      {
        ...TreeGridStatusColumn,
        hidden: !isExternal,
        customStyle() {
          return {
            'text-align': 'center'
          };
        },
        customClass(dataItem) {
          return dataItem.shouldShowStatus ? ['sticker', dataItem.status] : ' ';
        },
        getHtmlTemplate(dataItem) {
          return dataItem.shouldShowStatus ? dataItem.status : ' ';
        }
      },
      {
        ...TreeGridLinksColumn,
        getHtmlTemplate(dataItem) {
          return dataItem.links.toString();
        }
      },
      {
        ...TreeGridDataPrivacyColumn,
        hidden: !(isRefModel && GDPREnabled),
        getHtmlTemplate(dataItem) {
          return dataItem.gdprStatus;
        },
        customClass() {
          return ['secondary-grid-text'];
        }
      },
      {
        ...TreeGridOptimizeStatusColumn,
        hidden: !isExternal,
        getHtmlTemplate(dataItem) {
          return dataItem.optimizeStatus;
        },
        customClass() {
          return ['secondary-grid-text'];
        }
      },
      {
        ...TreeGridImpactColumn,
        hidden: !isExternal || (Boolean(orgInfo) && packageNotConnected),
        getHtmlTemplate(dataItem) {
          return dataItem.impact;
        },
        customClass(dataItem) {
          return ['impact-status', dataItem.impactClass];
        }
      },
      {
        ...TreeGridTagsColumn,
        ...(tagsColumnWidth > TreeGridTagsColumn.width && {width: tagsColumnWidth}),
        getHtmlTemplate(dataItem) {
          return !!dataItem.tags ? dataItem.tags : [];
        },
        customClass() {
          return ['secondary-grid-text', 'sticker', 'unchanged', 'q9-tag-cell'];
        }
      }
    ] as IGridColumn<ITreeNode>[];
  }

  public generateMyMetadataColumns(
    orgInfo: OrgInfo,
    refModel: Refmodel,
    tagsColumnWidth: number
  ): IGridColumn<ITreeNode>[] {
    const isRefmodel = Boolean(refModel);
    const isExternal = isRefmodel && refModel.external;
    const GDPREnabled = isRefmodel && refModel.gdprEnabled;
    const {packageVersion} = orgInfo || {};
    const packageNotConnected = packageVersion === constants.PACKAGE_NOT_CONNECTED;
    const isSf = Boolean(isRefmodel && refModel.sf);

    return [
      {
        ...MyMetadataTypeColumn,
        hidden: !isSf,
        getHtmlTemplate(dataItem) {
          return dataItem.type;
        }
      },
      {
        ...MyMetadataNameColumn,
        getHtmlTemplate(dataItem) {
          return dataItem.name;
        }
      },
      {
        ...MyMetadataStatusColumn,
        hidden: !isExternal,
        customStyle() {
          return {
            'text-align': 'center'
          };
        },
        customClass(dataItem) {
          return ['node-status', dataItem.status, 'no-resize'];
        },
        getHtmlTemplate(dataItem) {
          return dataItem.status;
        }
      },
      {
        ...MyMetadataLinksColumn,
        getHtmlTemplate(dataItem) {
          return dataItem.links.toString();
        }
      },
      {
        ...MyMetadataDataPrivacyColumn,
        hidden: !(isRefmodel && GDPREnabled),
        getHtmlTemplate(dataItem) {
          return dataItem.data;
        }
      },
      {
        ...MyMetadataOptimizeStatusColumn,
        hidden: !isExternal,
        getHtmlTemplate(dataItem) {
          return dataItem.optimizeStatus;
        }
      },
      {
        ...MyMetadataImpactColumn,
        hidden: !isExternal || (Boolean(orgInfo) && packageNotConnected),
        getHtmlTemplate(dataItem) {
          return dataItem.impact;
        },
        customClass(dataItem) {
          return dataItem.impactClass;
        }
      },
      {
        ...MyMetadataTagsColumn,
        ...(tagsColumnWidth > MyMetadataTagsColumn.width && {width: tagsColumnWidth}),
        getHtmlTemplate(dataItem) {
          return !!dataItem.tags ? dataItem.tags : [];
        },
        customClass() {
          return ['secondary-grid-text', 'q9-tag-cell'];
        }
      }
    ].filter(column => !get(column, 'hidden', false)) as IGridColumn<ITreeNode>[];
  }
}
